import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { LANGUAGES } from '../i18n';

// Supported languages mapping (only the 7 languages from i18n)
const SUPPORTED_LANGUAGE_CODES = LANGUAGES.map(lang => lang.code.toLowerCase());

// Create language map for easy lookup
const LANGUAGE_MAP = LANGUAGES.reduce((acc, lang) => {
  acc[lang.code.toLowerCase()] = {
    code: lang.code,
    label: lang.label,
    nativeName: lang.nativeName,
    shortLabel: lang.shortLabel
  };
  return acc;
}, {});

// Language code variations for matching voices (expanded for better voice matching)
const LANGUAGE_VARIANTS = {
  'en': ['en', 'en-us', 'en-gb', 'en-in', 'en-au', 'en-ca'],
  'hi': ['hi', 'hi-in', 'hin'],
  'te': ['te', 'te-in', 'tel'],
  'ta': ['ta', 'ta-in', 'tam'],
  'kn': ['kn', 'kn-in', 'kan'],
  'ml': ['ml', 'ml-in', 'mal'],
  'mr': ['mr', 'mr-in', 'mar'],
};

// Preferred voice names for each language (for clearer speech)
const PREFERRED_VOICES = {
  'en': ['Google UK English Female', 'Microsoft Zira', 'Microsoft David', 'Google US English'],
  'hi': ['Microsoft Hemant', 'Google हिन्दी', 'Microsoft Kalpana'],
  'te': ['Microsoft Chitra', 'Google తెలుగు'],
  'ta': ['Microsoft Valluvar', 'Google தமிழ்'],
  'kn': ['Microsoft Gagan', 'Google ಕನ್ನಡ'],
  'ml': ['Microsoft Sangeeta', 'Google മലയാളം'],
  'mr': ['Microsoft Madhur', 'Google मराठी'],
};

// Styled Components for Medical UI
const FloatingButton = styled.button`
  position: fixed;
  bottom: calc(2rem + 80px);
  right: 2rem;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  z-index: 10001;
  transition: all 0.3s ease;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus {
    outline: 3px solid rgba(5, 150, 105, 0.5);
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    width: 56px;
    height: 56px;
    bottom: calc(1.5rem + 70px);
    right: 1.5rem;
    font-size: 1.25rem;
  }

  @media (max-width: 480px) {
    width: 52px;
    height: 52px;
    bottom: calc(1rem + 60px);
    right: 1rem;
    font-size: 1.125rem;
  }
`;

const ControlPanel = styled.div`
  position: fixed;
  bottom: calc(8rem + 80px);
  right: 2rem;
  width: 360px;
  max-width: calc(100vw - 2rem);
  max-height: calc(100vh - 10rem);
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  padding: 1.5rem;
  z-index: 10002;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  border: 1px solid #e5e7eb;
  overflow-y: auto;
  overflow-x: hidden;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #059669;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #047857;
  }

  @media (max-width: 768px) {
    width: calc(100vw - 2rem);
    max-width: 400px;
    bottom: calc(7rem + 70px);
    right: 1rem;
    padding: 1.25rem;
    max-height: calc(100vh - 8rem);
  }

  @media (max-width: 480px) {
    padding: 1rem;
    gap: 0.875rem;
    max-height: calc(100vh - 6rem);
    bottom: calc(5rem + 60px);
  }
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const PanelTitle = styled.h3`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem;
  line-height: 1;
  transition: color 0.2s;

  &:hover {
    color: #111827;
  }

  &:focus {
    outline: 2px solid #059669;
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const ProgressIndicator = styled.div`
  background: #f3f4f6;
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  min-height: 60px;
  max-height: 120px;
  overflow-y: auto;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #374151;
`;

const CurrentSentence = styled.div`
  background: #d1fae5;
  padding: 0.5rem;
  border-radius: 6px;
  border-left: 3px solid #059669;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #065f46;
`;

const UpcomingText = styled.div`
  color: #6b7280;
  font-size: 0.8125rem;
`;

const ControlGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Select = styled.select`
  padding: 0.625rem;
  border: 2px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  color: #111827;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #059669;
  }

  &:hover {
    border-color: #d1d5db;
  }
`;

const SliderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Slider = styled.input`
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: #e5e7eb;
  outline: none;
  -webkit-appearance: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #059669;
    cursor: pointer;
    transition: background 0.2s;
  }

  &::-webkit-slider-thumb:hover {
    background: #047857;
  }

  &::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #059669;
    cursor: pointer;
    border: none;
    transition: background 0.2s;
  }

  &::-moz-range-thumb:hover {
    background: #047857;
  }
`;

const SliderValue = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #059669;
  min-width: 45px;
  text-align: right;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const ControlButton = styled.button`
  flex: 1;
  padding: 0.625rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #d1d5db;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus {
    outline: 2px solid #059669;
    outline-offset: 2px;
  }
`;

const PlayPauseButton = styled(ControlButton)`
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  color: white;
  border: none;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #047857 0%, #065f46 100%);
  }
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 0.75rem;
  color: #991b1b;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
`;

const StatusText = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  text-align: center;
  margin-top: 0.5rem;
`;

const LanguageIndicator = styled.div`
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const LanguageBadge = styled.span`
  background: rgba(255, 255, 255, 0.2);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
`;

// Note: optgroup and option are regular HTML elements, not styled components

// Indian languages mapping
const INDIAN_LANGUAGES = {
  'en-IN': 'English (India)',
  'hi-IN': 'Hindi (हिंदी)',
  'te-IN': 'Telugu (తెలుగు)',
  'ta-IN': 'Tamil (தமிழ்)',
  'kn-IN': 'Kannada (ಕನ್ನಡ)',
  'ml-IN': 'Malayalam (മലയാളം)',
  'mr-IN': 'Marathi (मराठी)',
  'gu-IN': 'Gujarati (ગુજરાતી)',
  'pa-IN': 'Punjabi (ਪੰਜਾਬੀ)',
  'bn-IN': 'Bengali (বাংলা)',
  'or-IN': 'Odia (ଓଡ଼ିଆ)',
  'as-IN': 'Assamese (অসমীয়া)',
  'ur-IN': 'Urdu (اردو)',
};

/**
 * TTSPlayer Component - Text-to-Speech accessibility feature
 * 
 * Features:
 * - Extracts readable text from page content
 * - Supports all 7 languages: English, Kannada, Tamil, Malayalam, Hindi, Marathi, Telugu
 * - Auto-detects user language and selects best voice
 * - Language indicator shows which language is currently speaking
 * - Voices grouped by language for easy selection
 * - Play/Pause, speed control, pitch control, volume control, voice selection
 * - Progress indicator with current sentence
 * - Skip to next/previous sentence
 * - Optimized for clarity with pitch and volume controls
 * - Preferred voice selection for each language (clearer voices)
 * - Pauses on scroll/interaction
 * - Handles dynamic content changes
 * - Skips sensitive data
 * - Persistent settings (localStorage) for rate, pitch, volume, and voice
 * - Keyboard accessible
 * - ARIA compliant
 * - Works offline
 * 
 * Usage:
 * - Add <TTSPlayer /> to your app (already integrated in App.js)
 * - Mark sensitive content with data-sensitive attribute:
 *   <div data-sensitive>Patient ID: ABC123</div>
 * - Skip specific elements with data-tts-skip attribute:
 *   <nav data-tts-skip>Navigation</nav>
 * - Or use class "tts-skip":
 *   <div class="tts-skip">Skip this content</div>
 * 
 * Keyboard Shortcuts:
 * - Spacebar: Play/Pause (when panel is open)
 * 
 * Browser Support:
 * - Chrome/Edge: Full support with all voices
 * - Firefox: Full support
 * - Safari: Full support (may have limited voices)
 * - Mobile browsers: Supported
 * 
 * Clarity Tips:
 * - Increase pitch (1.0-2.0) for clearer speech
 * - Adjust volume (0-100%) for comfortable listening
 * - Select preferred voices for each language (automatically selected)
 * - Adjust speed (0.5x-2.0x) for comfortable reading pace
 */
const TTSPlayer = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [rate, setRate] = useState(() => {
    const saved = localStorage.getItem('tts-rate');
    return saved ? parseFloat(saved) : 1.0;
  });
  const [currentText, setCurrentText] = useState('');
  const [sentences, setSentences] = useState([]);
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  const [error, setError] = useState(null);
  const [isSupported, setIsSupported] = useState(true);
  const [currentLanguage, setCurrentLanguage] = useState(null);
  const [pitch, setPitch] = useState(() => {
    const saved = localStorage.getItem('tts-pitch');
    return saved ? parseFloat(saved) : 1.0;
  });
  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem('tts-volume');
    return saved ? parseFloat(saved) : 1.0;
  });
  const [voicesByLanguage, setVoicesByLanguage] = useState({});

  const utteranceRef = useRef(null);
  const observerRef = useRef(null);
  const contentRef = useRef(null);

  // Check browser support
  useEffect(() => {
    if (!('speechSynthesis' in window)) {
      setIsSupported(false);
      setError('Text-to-Speech is not supported in your browser. Please use Chrome, Firefox, Safari, or Edge.');
      console.warn('[TTS] Speech Synthesis API not supported');
      return;
    }

    console.log('[TTS] Speech Synthesis API supported');
  }, []);

  // Load voices and organize by language
  useEffect(() => {
    if (!isSupported) return;

    const loadVoices = () => {
      const availableVoices = window.speechSynthesis.getVoices();
      console.log('[TTS] Available voices:', availableVoices.length);

      if (availableVoices.length === 0) {
        // Voices might not be loaded yet, try again
        setTimeout(loadVoices, 100);
        return;
      }

      // Filter voices to only show supported languages (7 languages from i18n)
      const filteredVoices = availableVoices.filter(voice => {
        const voiceLang = voice.lang.toLowerCase();
        // Check if voice language matches any of our supported languages
        return SUPPORTED_LANGUAGE_CODES.some(langCode => {
          const variants = LANGUAGE_VARIANTS[langCode] || [langCode];
          return variants.some(variant => voiceLang.includes(variant));
        });
      });

      // If no filtered voices, fallback to English voices
      const voicesToUse = filteredVoices.length > 0 ? filteredVoices : 
        availableVoices.filter(v => v.lang.toLowerCase().includes('en'));

      setVoices(voicesToUse);
      console.log('[TTS] Filtered voices:', voicesToUse.length, 'out of', availableVoices.length);

      // Organize voices by language for better selection
      const organized = {};
      voicesToUse.forEach(voice => {
        const voiceLang = voice.lang.toLowerCase();
        // Find which language this voice belongs to
        for (const langCode of SUPPORTED_LANGUAGE_CODES) {
          const variants = LANGUAGE_VARIANTS[langCode] || [langCode];
          if (variants.some(variant => voiceLang.includes(variant))) {
            if (!organized[langCode]) {
              organized[langCode] = [];
            }
            organized[langCode].push(voice);
            break;
          }
        }
      });
      setVoicesByLanguage(organized);

      // Auto-select voice based on user language with preference for clearer voices
      const userLang = (i18n.language || navigator.language || 'en').toLowerCase().split('-')[0];
      const userLangVariants = LANGUAGE_VARIANTS[userLang] || [userLang];
      
      console.log('[TTS] User language:', userLang);

      // Try to find a preferred voice first (clearer voices)
      let preferredVoice = null;
      const preferredNames = PREFERRED_VOICES[userLang] || [];
      
      for (const preferredName of preferredNames) {
        preferredVoice = voicesToUse.find(voice => 
          voice.name.includes(preferredName) || preferredName.includes(voice.name)
        );
        if (preferredVoice) break;
      }

      // If no preferred voice, try to find any matching voice for user's language
      if (!preferredVoice) {
        preferredVoice = voicesToUse.find(voice => {
          const voiceLang = voice.lang.toLowerCase();
          return userLangVariants.some(variant => voiceLang.includes(variant));
        });
      }

      // Fallback to English if exact match not found
      if (!preferredVoice) {
        preferredVoice = voicesToUse.find(
          voice => voice.lang.toLowerCase().includes('en')
        );
      }

      // Fallback to first available voice
      if (!preferredVoice && voicesToUse.length > 0) {
        preferredVoice = voicesToUse[0];
      }

      if (preferredVoice) {
        setSelectedVoice(preferredVoice);
        // Set current language based on selected voice
        const voiceLang = preferredVoice.lang.toLowerCase();
        for (const langCode of SUPPORTED_LANGUAGE_CODES) {
          const variants = LANGUAGE_VARIANTS[langCode] || [langCode];
          if (variants.some(variant => voiceLang.includes(variant))) {
            setCurrentLanguage(langCode);
            break;
          }
        }
        console.log('[TTS] Selected voice:', preferredVoice.name, preferredVoice.lang);
      }

      // Load saved voice preference
      const savedVoiceName = localStorage.getItem('tts-voice');
      if (savedVoiceName) {
        const savedVoice = availableVoices.find(v => v.name === savedVoiceName);
        if (savedVoice && voicesToUse.includes(savedVoice)) {
          setSelectedVoice(savedVoice);
          // Update current language
          const voiceLang = savedVoice.lang.toLowerCase();
          for (const langCode of SUPPORTED_LANGUAGE_CODES) {
            const variants = LANGUAGE_VARIANTS[langCode] || [langCode];
            if (variants.some(variant => voiceLang.includes(variant))) {
              setCurrentLanguage(langCode);
              break;
            }
          }
          console.log('[TTS] Restored saved voice:', savedVoiceName);
        }
      }
    };

    loadVoices();

    // Some browsers load voices asynchronously
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
  }, [isSupported, i18n.language]);

  // Extract readable text from page
  const extractPageText = useCallback(() => {
    try {
      // Skip elements that should not be read
      const skipSelectors = [
        'nav',
        'header',
        'footer',
        '[data-tts-skip]',
        '[data-sensitive]',
        'script',
        'style',
        'noscript',
        '.tts-skip',
        'input[type="password"]',
        'input[type="hidden"]',
      ];

      // Main content selectors (prioritize these)
      const mainContentSelectors = [
        'main',
        '[role="main"]',
        '.main-content',
        '.content',
        '#content',
        'article',
        '[role="article"]',
      ];

      let contentElement = null;

      // Try to find main content area
      for (const selector of mainContentSelectors) {
        const element = document.querySelector(selector);
        if (element) {
          contentElement = element;
          break;
        }
      }

      // Fallback to body if no main content found
      if (!contentElement) {
        contentElement = document.body;
      }

      // Clone the element to avoid modifying the original
      const clone = contentElement.cloneNode(true);

      // Remove skip elements
      skipSelectors.forEach(selector => {
        const elements = clone.querySelectorAll(selector);
        elements.forEach(el => el.remove());
      });

      // Extract text content
      let text = clone.textContent || clone.innerText || '';

      // Clean up text
      text = text
        .replace(/\s+/g, ' ') // Replace multiple spaces with single space
        .replace(/\n+/g, ' ') // Replace newlines with space
        .replace(/[\u{1F300}-\u{1F9FF}]/gu, '') // Remove emojis
        .replace(/[\u{2600}-\u{26FF}]/gu, '') // Remove misc symbols
        .replace(/[\u{2700}-\u{27BF}]/gu, '') // Remove dingbats
        .trim();

      // Split into sentences
      const sentenceRegex = /[.!?।॥]+/g;
      const sentenceArray = text
        .split(sentenceRegex)
        .map(s => s.trim())
        .filter(s => s.length > 0);

      console.log('[TTS] Extracted text:', {
        totalLength: text.length,
        sentenceCount: sentenceArray.length,
        preview: text.substring(0, 100) + '...',
      });

      return {
        fullText: text,
        sentences: sentenceArray,
      };
    } catch (error) {
      console.error('[TTS] Error extracting text:', error);
      setError('Failed to extract page text. Please try again.');
      return {
        fullText: '',
        sentences: [],
      };
    }
  }, []);

  // Handle play/pause
  const handlePlayPause = useCallback(() => {
    if (!isSupported || !selectedVoice) {
      setError('Text-to-Speech is not available. Please check your browser support.');
      return;
    }

    if (isPlaying) {
      // Pause
      window.speechSynthesis.cancel();
      setIsPlaying(false);
      console.log('[TTS] Paused');
    } else {
      // Play
      if (sentences.length === 0) {
        // Extract text if not already extracted
        const extracted = extractPageText();
        setCurrentText(extracted.fullText);
        setSentences(extracted.sentences);
        setCurrentSentenceIndex(0);

        if (extracted.sentences.length === 0) {
          setError('No readable text found on this page.');
          return;
        }
      }

      // Start reading from current sentence
      readSentence(currentSentenceIndex);
      setIsPlaying(true);
      console.log('[TTS] Started reading');
    }
  }, [isPlaying, isSupported, selectedVoice, sentences, currentSentenceIndex, extractPageText]);

  // Read a specific sentence
  const readSentence = useCallback((index) => {
    if (!selectedVoice || sentences.length === 0 || index < 0 || index >= sentences.length) {
      setIsPlaying(false);
      return;
    }

    const sentence = sentences[index];
    setCurrentSentenceIndex(index);

    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    // Small delay to ensure cancellation is processed
    setTimeout(() => {
      // Create new utterance with optimized settings for clarity
      const utterance = new SpeechSynthesisUtterance(sentence);
      utterance.voice = selectedVoice;
      utterance.rate = rate; // User-controlled speed
      utterance.pitch = pitch; // User-controlled pitch (1.0 = normal, higher = clearer)
      utterance.volume = volume; // User-controlled volume (1.0 = max)
      utterance.lang = selectedVoice.lang || 'en';
      
      // Additional settings for better clarity
      // Note: Some browsers may not support all these properties
      if (utterance.textContent === undefined) {
        // Ensure text is properly set
        utterance.text = sentence;
      }

      // Event handlers
      utterance.onstart = () => {
        console.log('[TTS] Started sentence', index, ':', sentence.substring(0, 50));
        setError(null); // Clear any previous errors
      };

      utterance.onend = () => {
        console.log('[TTS] Ended sentence', index);
        
        // Move to next sentence (check isPlaying state via closure)
        setIsPlaying(current => {
          if (!current) return false;
          
          if (index < sentences.length - 1) {
            setTimeout(() => {
              readSentence(index + 1);
            }, 100);
          } else {
            // Finished reading all sentences
            setCurrentSentenceIndex(0);
            console.log('[TTS] Finished reading all sentences');
            return false;
          }
          return current;
        });
      };

      utterance.onerror = (event) => {
        console.error('[TTS] Speech error:', event.error, event.type);
        
        // Handle specific error types
        if (event.error === 'interrupted' || event.error === 'canceled') {
          // These are expected when user pauses/cancels, don't show error
          return;
        }
        
        // Only show error for actual problems
        if (event.error === 'synthesis-failed' || event.error === 'synthesis-unavailable') {
          setError('Voice synthesis failed. Please try a different voice or refresh the page.');
          setIsPlaying(false);
        } else if (event.error === 'audio-busy') {
          // Audio system busy, try again after a short delay
          setTimeout(() => {
            setIsPlaying(current => {
              if (current) {
                readSentence(index);
              }
              return current;
            });
          }, 500);
        } else {
          // Other errors - log but don't always show to user
          console.warn('[TTS] Speech error (non-critical):', event.error);
          // Don't stop playback for minor errors
        }
      };

      utteranceRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    }, 50);
  }, [selectedVoice, rate, pitch, volume, sentences]);

  // Handle voice change
  const handleVoiceChange = useCallback((event) => {
    const voiceName = event.target.value;
    const voice = voices.find(v => v.name === voiceName);
    
    if (voice) {
      setSelectedVoice(voice);
      localStorage.setItem('tts-voice', voice.name);
      
      // Update current language based on selected voice
      const voiceLang = voice.lang.toLowerCase();
      for (const langCode of SUPPORTED_LANGUAGE_CODES) {
        const variants = LANGUAGE_VARIANTS[langCode] || [langCode];
        if (variants.some(variant => voiceLang.includes(variant))) {
          setCurrentLanguage(langCode);
          break;
        }
      }
      
      console.log('[TTS] Voice changed to:', voice.name, voice.lang);

      // Restart reading if currently playing
      if (isPlaying) {
        window.speechSynthesis.cancel();
        setTimeout(() => {
          readSentence(currentSentenceIndex);
        }, 100);
      }
    }
  }, [voices, isPlaying, currentSentenceIndex, readSentence]);

  // Debounce rate change to avoid restarting on every slider movement
  const rateChangeTimeoutRef = useRef(null);
  const handleRateChange = useCallback((event) => {
    const newRate = parseFloat(event.target.value);
    setRate(newRate);
    localStorage.setItem('tts-rate', newRate.toString());

    // Clear existing timeout
    if (rateChangeTimeoutRef.current) {
      clearTimeout(rateChangeTimeoutRef.current);
    }

    // Only restart if playing and after user stops moving slider (500ms delay)
    if (isPlaying && utteranceRef.current) {
      rateChangeTimeoutRef.current = setTimeout(() => {
        if (isPlaying) {
          window.speechSynthesis.cancel();
          setTimeout(() => {
            readSentence(currentSentenceIndex);
          }, 100);
        }
      }, 500);
    }
  }, [isPlaying, currentSentenceIndex, readSentence]);

  // Handle pitch change
  const handlePitchChange = useCallback((event) => {
    const newPitch = parseFloat(event.target.value);
    setPitch(newPitch);
    localStorage.setItem('tts-pitch', newPitch.toString());

    // Clear existing timeout
    if (rateChangeTimeoutRef.current) {
      clearTimeout(rateChangeTimeoutRef.current);
    }

    // Only restart if playing and after user stops moving slider (500ms delay)
    if (isPlaying && utteranceRef.current) {
      rateChangeTimeoutRef.current = setTimeout(() => {
        if (isPlaying) {
          window.speechSynthesis.cancel();
          setTimeout(() => {
            readSentence(currentSentenceIndex);
          }, 100);
        }
      }, 500);
    }
  }, [isPlaying, currentSentenceIndex, readSentence]);

  // Handle volume change
  const handleVolumeChange = useCallback((event) => {
    const newVolume = parseFloat(event.target.value);
    setVolume(newVolume);
    localStorage.setItem('tts-volume', newVolume.toString());

    // Update current utterance volume if playing
    if (isPlaying && utteranceRef.current) {
      utteranceRef.current.volume = newVolume;
    }
  }, [isPlaying]);

  // Skip to next sentence
  const handleNext = useCallback(() => {
    if (currentSentenceIndex < sentences.length - 1) {
      window.speechSynthesis.cancel();
      readSentence(currentSentenceIndex + 1);
    }
  }, [currentSentenceIndex, sentences.length, readSentence]);

  // Skip to previous sentence
  const handlePrevious = useCallback(() => {
    if (currentSentenceIndex > 0) {
      window.speechSynthesis.cancel();
      readSentence(currentSentenceIndex - 1);
    }
  }, [currentSentenceIndex, readSentence]);

  // Pause on explicit user actions (but not on scroll or minor interactions)
  useEffect(() => {
    if (!isPlaying) return;

    // Only pause on clicks outside the TTS panel or explicit stop actions
    const handleClickOutside = (event) => {
      const target = event.target;
      const ttsPanel = document.querySelector('[role="dialog"]');
      const ttsButton = document.querySelector('[aria-label*="Text-to-Speech"]');
      
      // Don't pause if clicking inside TTS panel or button
      if (ttsPanel && ttsPanel.contains(target)) return;
      if (ttsButton && ttsButton.contains(target)) return;
      
      // Only pause on significant clicks (not on every click)
      if (target.tagName === 'BUTTON' || target.tagName === 'A' || target.closest('button') || target.closest('a')) {
        window.speechSynthesis.pause();
        console.log('[TTS] Paused due to user action');
      }
    };

    // Don't pause on scroll - let users scroll while listening
    // Only pause on explicit navigation or form submissions
    const handleFormSubmit = (event) => {
      if (event.target.tagName === 'FORM') {
        window.speechSynthesis.cancel();
        setIsPlaying(false);
      }
    };

    document.addEventListener('click', handleClickOutside, true);
    document.addEventListener('submit', handleFormSubmit, true);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('submit', handleFormSubmit, true);
    };
  }, [isPlaying]);

  // Handle dynamic content changes
  useEffect(() => {
    if (!isOpen) return;

    const observeContent = () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      const targetNode = document.querySelector('main') || document.body;
      
      observerRef.current = new MutationObserver((mutations) => {
        // If content changed significantly, re-extract text
        const hasSignificantChange = mutations.some(mutation => {
          return mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0;
        });

        if (hasSignificantChange && !isPlaying) {
          console.log('[TTS] Content changed, re-extracting text');
          const extracted = extractPageText();
          setCurrentText(extracted.fullText);
          setSentences(extracted.sentences);
          setCurrentSentenceIndex(0);
        }
      });

      observerRef.current.observe(targetNode, {
        childList: true,
        subtree: true,
      });
    };

    observeContent();

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [isOpen, isPlaying, extractPageText]);

  // Keyboard accessibility
  useEffect(() => {
    const handleKeyPress = (event) => {
      // Spacebar to play/pause (when panel is open)
      if (event.code === 'Space' && isOpen && event.target.tagName !== 'INPUT' && event.target.tagName !== 'TEXTAREA') {
        event.preventDefault();
        handlePlayPause();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [isOpen, handlePlayPause]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (rateChangeTimeoutRef.current) {
        clearTimeout(rateChangeTimeoutRef.current);
      }
    };
  }, []);

  if (!isSupported) {
    return null; // Don't render if not supported
  }

  const currentSentence = sentences[currentSentenceIndex] || '';
  const upcomingText = sentences.slice(currentSentenceIndex + 1, currentSentenceIndex + 3).join(' ');

  // Voices are already filtered to supported languages in loadVoices
  const availableVoices = voices;

  return (
    <>
      {/* Read Aloud button temporarily hidden - kept for future use */}
      {/* <FloatingButton
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Open Text-to-Speech controls"
        aria-expanded={isOpen}
        title="Text-to-Speech"
      >
        🔊
      </FloatingButton> */}

      {isOpen && (
        <ControlPanel
          role="dialog"
          aria-labelledby="tts-title"
          aria-modal="true"
        >
          <PanelHeader>
            <PanelTitle id="tts-title">Text-to-Speech</PanelTitle>
            <CloseButton
              onClick={() => {
                setIsOpen(false);
                if (isPlaying) {
                  window.speechSynthesis.cancel();
                  setIsPlaying(false);
                }
              }}
              aria-label="Close Text-to-Speech panel"
            >
              ×
            </CloseButton>
          </PanelHeader>

          {error && (
            <ErrorMessage role="alert">
              {error}
            </ErrorMessage>
          )}

          <ProgressIndicator>
            {currentSentence && (
              <CurrentSentence aria-live="polite" aria-atomic="true">
                {currentSentence}
              </CurrentSentence>
            )}
            {upcomingText && (
              <UpcomingText>
                {upcomingText.substring(0, 150)}...
              </UpcomingText>
            )}
            {!currentSentence && !upcomingText && (
              <div style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                Click play to start reading the page
              </div>
            )}
          </ProgressIndicator>

          {currentLanguage && (
            <LanguageIndicator role="status" aria-live="polite">
              <span>🔊 Speaking:</span>
              <LanguageBadge>
                {LANGUAGE_MAP[currentLanguage]?.nativeName || LANGUAGE_MAP[currentLanguage]?.label || currentLanguage.toUpperCase()}
              </LanguageBadge>
            </LanguageIndicator>
          )}

          <ControlGroup>
            <Label htmlFor="tts-voice">
              Voice / Language ({voices.length} available)
            </Label>
            <Select
              id="tts-voice"
              value={selectedVoice?.name || ''}
              onChange={handleVoiceChange}
              aria-label="Select voice and language"
            >
              {Object.keys(voicesByLanguage).length > 0 ? (
                // Group voices by language
                Object.keys(voicesByLanguage).sort().map(langCode => {
                  const langInfo = LANGUAGE_MAP[langCode];
                  const langVoices = voicesByLanguage[langCode];
                  return (
                    <optgroup key={langCode} label={`${langInfo?.nativeName || langInfo?.label || langCode} (${langVoices.length})`}>
                      {langVoices.map((voice) => (
                        <option key={voice.name} value={voice.name}>
                          {voice.name} {voice.default ? '(Default)' : ''}
                        </option>
                      ))}
                    </optgroup>
                  );
                })
              ) : (
                // Fallback: show all voices in a flat list
                availableVoices.map((voice) => (
                  <option key={voice.name} value={voice.name}>
                    {voice.name} ({voice.lang})
                  </option>
                ))
              )}
            </Select>
          </ControlGroup>

          <ControlGroup>
            <Label htmlFor="tts-rate">
              Speed: <SliderValue>{rate.toFixed(1)}x</SliderValue>
            </Label>
            <SliderContainer>
              <Slider
                id="tts-rate"
                type="range"
                min="0.5"
                max="2"
                step="0.1"
                value={rate}
                onChange={handleRateChange}
                aria-label="Reading speed"
              />
            </SliderContainer>
          </ControlGroup>

          <ControlGroup>
            <Label htmlFor="tts-pitch">
              Pitch: <SliderValue>{pitch.toFixed(1)}</SliderValue>
            </Label>
            <SliderContainer>
              <Slider
                id="tts-pitch"
                type="range"
                min="0.5"
                max="2"
                step="0.1"
                value={pitch}
                onChange={handlePitchChange}
                aria-label="Voice pitch (higher = clearer)"
              />
            </SliderContainer>
            <StatusText style={{ fontSize: '0.7rem', marginTop: '0.25rem' }}>
              Higher pitch can improve clarity
            </StatusText>
          </ControlGroup>

          <ControlGroup>
            <Label htmlFor="tts-volume">
              Volume: <SliderValue>{Math.round(volume * 100)}%</SliderValue>
            </Label>
            <SliderContainer>
              <Slider
                id="tts-volume"
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={handleVolumeChange}
                aria-label="Volume level"
              />
            </SliderContainer>
          </ControlGroup>

          <ButtonGroup>
            <ControlButton
              onClick={handlePrevious}
              disabled={currentSentenceIndex === 0 || sentences.length === 0}
              aria-label="Previous sentence"
            >
              ⏮ Previous
            </ControlButton>
            <PlayPauseButton
              onClick={handlePlayPause}
              disabled={!selectedVoice}
              aria-label={isPlaying ? 'Pause reading' : 'Start reading'}
            >
              {isPlaying ? '⏸ Pause' : '▶ Play'}
            </PlayPauseButton>
            <ControlButton
              onClick={handleNext}
              disabled={currentSentenceIndex >= sentences.length - 1 || sentences.length === 0}
              aria-label="Next sentence"
            >
              Next ⏭
            </ControlButton>
          </ButtonGroup>

          <StatusText>
            {sentences.length > 0 && (
              <>
                Sentence {currentSentenceIndex + 1} of {sentences.length}
                {isPlaying && ' • Reading...'}
              </>
            )}
            {sentences.length === 0 && !error && 'Ready to read'}
          </StatusText>
        </ControlPanel>
      )}
    </>
  );
};

export default TTSPlayer;

